import { clampPct } from './leaderboardRows';
import type { BattleContender } from './types';

export type BattleStanding = {
  contender: BattleContender;
  /**
   * Share of the battle total, 0–100, as core sent it: core already applied the run's max-gap
   * compression, so the client shows it as is. `null` becomes 0.
   */
  sharePct: number;
  /** The gold card. Nobody leads before a bet has scored, so all-zero standings have no leader. */
  isLeader: boolean;
  /**
   * 1-based place by real share. Equal shares share a place (1, 1, 3), so a tie — every slot at 0%
   * before the first bet — draws the cards at the same height.
   */
  rank: number;
  /**
   * 1-based place shown under the card (GS-1032): by share, a tie broken by the lower `display_order` as
   * settlement breaks it, so every card has its own number.
   */
  place: number;
};

/** Settlement's ordering: higher real share first, then the lower `display_order`. */
const compareByPlace = (a: BattleContender, b: BattleContender) =>
  (b.sharePct ?? 0) - (a.sharePct ?? 0) || a.displayOrder - b.displayOrder;

/**
 * The leading slot: highest real share, ties broken by the lower `display_order` — the same rule
 * settlement uses, so the pane and the payout can never name different winners.
 */
export function selectLeadingContender(contenders: BattleContender[]): BattleContender | null {
  return contenders.reduce<BattleContender | null>(
    (leader, contender) => (!leader || compareByPlace(contender, leader) < 0 ? contender : leader),
    null,
  );
}

/** Standings in render order (`display_order`), each with its place by share. */
export function buildStandings(contenders: BattleContender[]): BattleStanding[] {
  const leader = selectLeadingContender(contenders);
  const leaderShare = clampPct(leader?.sharePct ?? 0);
  const shares = contenders.map((contender) => clampPct(contender.sharePct ?? 0));
  const byPlace = [...contenders].sort(compareByPlace);

  return [...contenders]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((contender) => {
      const sharePct = clampPct(contender.sharePct ?? 0);
      return {
        contender,
        sharePct,
        isLeader: leaderShare > 0 && contender.gameId === leader?.gameId,
        rank: shares.filter((share) => share > sharePct).length + 1,
        place: byPlace.indexOf(contender) + 1,
      };
    });
}
